---
title: "Importing borrowers from SIMS, Arbor or Bromcom"
slug: "importing-from-sims-arbor-bromcom"
category: "borrowers"
description: "Export a CSV from your school MIS and bring every pupil into BookBurrow in a single step."
tags: ["sims", "arbor", "bromcom", "import", "borrowers", "csv", "mis"]
order: 1
---

Typing pupils in one at a time is miserable, so don't. Every UK school MIS can export a pupil list as a CSV, and BookBurrow's importer is built to match the columns those systems produce. For a typical primary school, the whole thing takes under five minutes.

## What you'll end up with

For every pupil in your CSV, BookBurrow will create a borrower record with:

- Full name
- Year group
- Class (if your CSV includes it)
- A borrower card number (either from the CSV, or generated automatically)

Any field BookBurrow doesn't recognise is simply ignored — it won't complain about extra columns, and it won't store anything it doesn't need.

## Step 1 — Export a CSV from your MIS

### SIMS

<Steps>
  <Step title="Open Reports → Design Report">
    Create a report based on **Student** as the data source. Add columns: Legal Forename, Legal Surname, Year Group, Reg Group, Admission Number.
  </Step>
  <Step title="Run the report">
    Use **Run Report** and choose **Export → CSV**. Save the file somewhere you can find it — the Downloads folder is fine.
  </Step>
</Steps>

If your SIMS already has a pre-built "Student Contact List" report, you can use that — just make sure it includes at least first name, surname, and year group.

### Arbor

<Steps>
  <Step title="Open Students → All Students">
    This gives you the full pupil list with filters along the top.
  </Step>
  <Step title="Click Export → CSV">
    Choose the columns to include. Ticking **First name**, **Last name**, **Year**, **Form** and **MIS ID** is enough.
  </Step>
</Steps>

### Bromcom

<Steps>
  <Step title="Open Students → Student List">
    Filter to the cohort you want (usually "All current students").
  </Step>
  <Step title="Click Actions → Export to CSV">
    Bromcom's export already includes name, year group and registration group by default.
  </Step>
</Steps>

## Step 2 — Import it into BookBurrow

<Steps>
  <Step title="Open Borrowers → Import">
    The button is in the top-right of the Borrowers tab.
  </Step>
  <Step title="Pick your CSV file">
    BookBurrow reads the first few rows and tries to match your column headers to its own fields.
  </Step>
  <Step title="Check the column mapping">
    You'll see each column in your CSV with a dropdown of what it maps to. If BookBurrow got one wrong (e.g. it mapped "Reg Group" to Class but you want it to be ignored), change the dropdown.
  </Step>
  <Step title="Preview the first ten rows">
    The preview shows exactly what will be saved for the first ten pupils. If those look right, the rest will too.
  </Step>
  <Step title="Click Import">
    BookBurrow shows a progress bar and, when it's done, a summary: how many borrowers were added, how many were skipped (duplicates), and any warnings.
  </Step>
</Steps>

<Callout variant="warning" title="Run this once, not twice">
If you import the same CSV twice, BookBurrow will detect duplicates by MIS ID or by name + year group, and **skip** them rather than creating a second record. So running an import by accident won't create a mess — but you can also safely run the importer each September to pull in new starters only.
</Callout>

## Card numbers

Most schools don't print proper library cards on day one — pupils just use their existing ID cards, or BookBurrow-generated numbers on a piece of card. The importer can handle both:

- If your CSV has a column you'd like to use as the card number (for example, the pupil's existing ID badge number), map it to **Card number** in the importer.
- Otherwise, BookBurrow will generate a sequential card number for each pupil, starting from whatever number you set in **Settings → Borrowers → Next card number**.

## What if something goes wrong?

Nothing is saved until you click **Import** at the end, so you can abandon the process at any point. Even after importing, you can delete any borrower from the Borrowers tab. If you want to completely undo an import, **Settings → Borrowers → Undo last import** removes every record created by the most recent import.

## Next steps

- **[Year-end promotion](/help/guides/year-end-promotion)** — how to bump everyone up a year group at the end of the school year instead of re-importing.
- **[Printing borrower cards](/help/guides/printing-borrower-cards)** — if you want to make proper library cards with barcodes.
